import React, { useContext, useState } from "react";
import { Select, SelectOnChangeData } from "@fluentui/react-components";
import ConfigureProjectDialog from "./ConfigureProjectDialog";
import { IProject, IUser } from "../types/types";
import { ApiContext, ICompany } from "../providers/ApiProvider";

interface IProjectSettings {
    user: IUser;
    jwtoken: string;
    companiesList: string[];
    companiesData: ICompany[];
    setMessage: (msg: string) => void;
    setSuccess: (success: boolean) => void;
    onProjectUpdated?: () => void; // Callback to refresh project list after update
}

export default function ProjectSettings(props: IProjectSettings) {
    const { getCompanyByName, getProjects } = useContext(ApiContext);

    const [isSelected, setIsSelected] = useState<boolean>(false);
    const [projectsData, setProjectsData] = useState<IProject[]>([]);
    const [projectsList, setProjectsList] = useState<string[]>([]);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [selectedProjectName, setSelectedProjectName] = useState<string | null>();
    const [selectedCompany, setSelectedCompany] = useState<string | null>();

    const clearStates = async () => {
        setIsSelected(false);
        setSelectedProjectName(null);
        // Note: Project list refresh is handled by the callback passed to ConfigureProjectDialog
        // to avoid duplicate API calls
    };

    const getCompanyID = async (companyName: string) => {
        try {
            // First try to find the company in the already-loaded companiesData
            const company = props.companiesData?.find((c) => c.company_name === companyName);
            
            if (company && company.id) {
                _getProjects(company.id);
            } else {
                // Fallback: try API call if not found in companiesData
                try {
                    const apiCompany = await getCompanyByName(props.jwtoken, companyName);
                    
                    // Handle the case where API returns wrong structure (hasHours response)
                    if (apiCompany && apiCompany.id) {
                        _getProjects(apiCompany.id);
                    } else {
                        // If API returns hasHours structure, try to find company ID from projectCompanyID array
                        const apiResponse = apiCompany as any;
                        if (apiResponse && apiResponse.projectCompanyID && Array.isArray(apiResponse.projectCompanyID)) {
                            const projectCompany = apiResponse.projectCompanyID.find(
                                (pci: any) => pci.company_name === companyName
                            );
                            if (projectCompany && projectCompany.company_id) {
                                _getProjects(projectCompany.company_id);
                            } else {
                                console.error("Could not find company ID in API response");
                                setIsDisabled(true);
                                setProjectsList([]);
                                setProjectsData([]);
                            }
                        } else {
                            console.error("Company object missing or invalid:", apiCompany);
                            setIsDisabled(true);
                            setProjectsList([]);
                            setProjectsData([]);
                        }
                    }
                } catch (apiError) {
                    console.error("Error fetching company from API:", apiError);
                    setIsDisabled(true);
                    setProjectsList([]);
                    setProjectsData([]);
                }
            }
        } catch (error) {
            console.error("Error in getCompanyID:", error);
            setIsDisabled(true);
            setProjectsList([]);
            setProjectsData([]);
        }
    };

    const _getProjects = (selectedCompanyID: string) => {
        setIsDisabled(true); // Disable while loading
        getProjects(props.jwtoken, selectedCompanyID)
            .then((projects) => {
                const listOfProjects: string[] = [];
                let disabled = false;

                if (projects && Array.isArray(projects)) {
                    // Sort projects: locked/disabled projects first, then alphabetically
                    const sortedProjects = [...projects].sort((a, b) => {
                        // If both are locked or both are not locked, sort alphabetically
                        if (a.entry_locked === b.entry_locked) {
                            return a.project_name.localeCompare(b.project_name);
                        }
                        // Locked projects come first
                        return a.entry_locked ? -1 : 1;
                    });

                    // Use a Set to avoid duplicates, but log if duplicates are found
                    const seenNames = new Set<string>();
                    sortedProjects.forEach((project) => {
                        if (seenNames.has(project.project_name)) {
                            console.warn(`Duplicate project name found: ${project.project_name}`);
                        } else {
                            seenNames.add(project.project_name);
                            // Include ALL projects - admins should see all projects regardless of membership
                            listOfProjects.push(project.project_name);
                        }
                    });

                    if (listOfProjects.length === 0) {
                        disabled = true;
                    }
                } else {
                    console.warn("Projects response is not an array:", projects);
                    disabled = true;
                }

                setProjectsList(listOfProjects);
                setProjectsData(projects || []);
                setIsDisabled(disabled);
            })
            .catch((err) => {
                console.error("Error fetching projects:", err);
                setIsDisabled(true);
                setProjectsList([]);
                setProjectsData([]);
            });
    };

    return (
        <>
            {/* <Text color={"red"} weight="bold" content={"<ADMINS ONLY> Change project settings"} /> */}
            <div className="w-full">
                <div className="mb-3">
                    <Select
                        className="w-full"
                        disabled={!props.user.is_admin && !props.user.is_project_manager}
                        value={selectedCompany || ""}
                        onChange={(_e, data: SelectOnChangeData) => {
                            if (data.value && data.value !== "") {
                                getCompanyID(data.value as string);
                                setSelectedCompany(data.value as string);
                            } else {
                                setSelectedCompany(null);
                                setSelectedProjectName(null);
                                setIsDisabled(true);
                                setProjectsList([]);
                                setProjectsData([]);
                            }
                        }}
                    >
                        <option value="" disabled>
                            Select a company...
                        </option>
                        {props.companiesList.map((company) => (
                            <option key={company} value={company}>
                                {company}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 items-start">
                    <div className="flex-1 w-full sm:w-auto min-w-0">
                        <Select
                            className="w-full"
                            disabled={isDisabled}
                            value={selectedProjectName ? `${selectedProjectName}${projectsData.find((p) => p.project_name === selectedProjectName)?.entry_locked ? " 🔒 (Locked)" : ""}` : ""}
                            onChange={(_e, data: SelectOnChangeData) => {
                                if (data.value && data.value !== "") {
                                    // Strip the lock indicator from the selected value to get the actual project name
                                    const actualProjectName = (data.value as string).replace(/\s*🔒\s*\(Locked\)\s*$/, "").trim();
                                    setIsSelected(true);
                                    setSelectedProjectName(actualProjectName);
                                } else {
                                    setIsSelected(false);
                                    setSelectedProjectName(null);
                                }
                            }}
                        >
                            {!selectedCompany ? (
                                <option value="" disabled>
                                    Select a company first
                                </option>
                            ) : projectsList.length > 0 ? (
                                <>
                                    <option value="" disabled>
                                        Select a project...
                                    </option>
                                    {projectsList.map((projectName) => {
                                        // Find the project data to check if it's locked
                                        const project = projectsData.find((p) => p.project_name === projectName);
                                        const isLocked = project?.entry_locked || false;
                                        
                                        return (
                                            <option key={projectName} value={projectName}>
                                                {projectName}{isLocked ? " 🔒 (Locked)" : ""}
                                            </option>
                                        );
                                    })}
                                </>
                            ) : (
                                <option value="" disabled>
                                    No projects available for this company
                                </option>
                            )}
                        </Select>
                    </div>
                    <div className="w-full sm:w-auto flex">
                        <ConfigureProjectDialog
                            jwtoken={props.jwtoken}
                            user={props.user}
                            projectName={selectedProjectName}
                            projectId={projectsData.find((project) => project.project_name === selectedProjectName)?.id}
                            selected={isSelected}
                            setMessage={props.setMessage}
                            setSuccess={props.setSuccess}
                            clearStates={async () => {
                                await clearStates();
                                // Refresh project list after update to get latest entry_locked status
                                if (selectedCompany) {
                                    await getCompanyID(selectedCompany);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
